import moment from "moment";
import Form, { Field as BaseField } from "rc-field-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/Button";
import Checkbox from "../../../../components/Inputs/Checkbox";
import NumberInput from "../../../../components/Inputs/NumberInput";
import Field from "../../../../components/Field";
import DateInput from "../../../../components/Inputs/DateInput";
import ResourceSelect from "../../../../components/Inputs/ResourceSelect";
import Select from "../../../../components/Inputs/Select";
import useApi from "../../../../utils/api";
import { formatDatesInText } from "../../../../utils/helpers/date";
import {
	getPathWithParams,
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../../utils/helpers/settings";
import { useCompany, useCompanyDateFormat } from "../../../../utils/hooks/company";
import { payCodeCalcFields } from "../../../Settings/PayPolicies/CalculationGroup/helper";

const DATE_OPTION = {
	TODAY: "today",
	END_OF_PAY_PERIOD: "end-of-pay-period",
	CUSTOM: "custom",
};

const hours = (value) => Number(value || 0).toFixed(2);

const payCodeIdOf = (payCode) => (typeof payCode === "object" ? payCode?.id : payCode);

/**
 * PTO payout of one plan (`mode: "payout"`, the row's plan) or of every active plan
 * (`mode: "payout-all"`). Settings works the amount out as of the payout date (the balance at the
 * last lock plus what the open pay periods accrue up to it, less time off), shown in a preview
 * before anything is written; a date after today is scheduled and worked out on that day.
 */
function PayoutForm({ mode, close, values, allPlans, loading, previewPayout, payout }) {
	const { t } = useTranslation();
	const [form] = Form.useForm();
	const { authGet } = useApi();
	const company = useCompany();
	const dateFormat = useCompanyDateFormat() || "MM/DD/YYYY";
	const [preview, setPreview] = useState(null);
	const [previewing, setPreviewing] = useState(false);

	const plans = useMemo(() => {
		if (mode === "payout-all") {
			return (allPlans || []).filter((plan) => plan.status === "active");
		}
		return values ? [values] : [];
	}, [mode, allPlans, values]);

	const unitPayCodePath = useMemo(
		() => getPathWithParams("/pay-code", true, {}, payCodeCalcFields, { hourType: "unit" }),
		[],
	);

	const requestOf = useCallback((data) => ({
		mode: data.mode,
		...(data.mode === DATE_OPTION.CUSTOM ? { date: moment(data.date).format("YYYY-MM-DD") } : {}),
		plans: plans.map((plan) => {
			// empty: everything the plan can pay out on the date
			const hoursAsked = data.hours?.[plan.id];
			return {
				userTimeOffPlan: plan.id,
				payCode: data.payCodes?.[plan.id]?.id,
				...(hoursAsked !== undefined && hoursAsked !== null && hoursAsked !== "" ? { hours: Number(String(hoursAsked).replace(/,/g, "")) } : {}),
			};
		}),
		close: !!data.close,
	}), [plans]);

	const onFinish = useCallback(async (data) => {
		const request = requestOf(data);
		if (!preview) {
			setPreviewing(true);
			setPreview((await previewPayout(request)) || null);
			setPreviewing(false);
			return;
		}
		payout(request);
	}, [preview, previewPayout, payout, requestOf]);

	// each plan's own pay code is the default when it is a unit pay code (TR-659)
	useEffect(() => {
		let cancelled = false;
		const init = async () => {
			form.resetFields();
			form.setFieldsValue({ mode: DATE_OPTION.TODAY, date: undefined, payCodes: {}, hours: {}, close: false });
			setPreview(null);
			if (!plans.length) {
				return;
			}
			let unitPayCodes = [];
			try {
				const response = await authGet(unitPayCodePath, { params: { pagination: false, company: company?.id } });
				unitPayCodes = response?.result || [];
			} catch (_) {
				// the pay codes stay empty and have to be chosen
			}
			if (cancelled) {
				return;
			}
			const payCodes = {};
			for (const plan of plans) {
				const match = unitPayCodes.find((payCode) => payCode.id === payCodeIdOf(plan.plan?.payCode));
				if (match) {
					payCodes[plan.id] = match;
				}
			}
			form.setFieldsValue({ payCodes });
		};
		init();
		return () => {
			cancelled = true;
		};
	}, [form, plans, authGet, unitPayCodePath, company?.id]);

	const blocked = !!preview?.reasons?.length;

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={onFinish}
			// the confirmed preview must be the one on screen: any change asks for a new one
			onValuesChange={() => setPreview(null)}
			scrollToFirstError
		>
			<Field
				name="mode"
				label={t("payout-date")}
				rules={[{ required: true, message: t("this-field-is-required") }]}
			>
				<Select
					placeholder={t("payout-date")}
					options={[
						{ label: t("today"), value: DATE_OPTION.TODAY },
						{ label: t("end-of-pay-period"), value: DATE_OPTION.END_OF_PAY_PERIOD },
						{ label: t("custom-date"), value: DATE_OPTION.CUSTOM },
					]}
				/>
			</Field>

			<BaseField shouldUpdate={(prev, next) => prev.mode !== next.mode}>
				{(_control, _meta, { getFieldValue }) => {
					if (getFieldValue("mode") !== DATE_OPTION.CUSTOM) {
						return null;
					}
					return (
						<Field
							name="date"
							label={t("custom-date")}
							rules={[{ required: true, message: t("this-field-is-required") }]}
							className="d-flex flex-column"
						>
							<DateInput placeholderText={t("custom-date")} />
						</Field>
					);
				}}
			</BaseField>

			{plans.map((plan) => (
				<div key={plan.id} className="d-flex">
					<Field
						className="flex-grow-1 mr-2"
						name={["payCodes", plan.id]}
						label={plans.length > 1 ? t("payout-pay-code-for", { plan: plan.plan?.code }) : t("pay-code")}
						rules={[{ required: true, message: t("this-field-is-required") }]}
					>
						<ResourceSelect
							labelPropName="description"
							resourcePath={unitPayCodePath}
							renderLabel={renderSettingsResourceLabel}
							placeholder={t("pay-code")}
							hasSearch
							getSearchFilters={getSettingsResourceSearchFilters}
						/>
					</Field>
					<Field name={["hours", plan.id]} label={t("payout-hours")} style={{ width: 130 }}>
						<NumberInput placeholder={t("payout-hours-all")} />
					</Field>
				</div>
			))}

			{plans.length > 0 && (
				<Field name="close" valuePropName="checked">
					<Checkbox id="payout-close">{t("payout-close-plans")}</Checkbox>
				</Field>
			)}

			{!plans.length && <div className="text-muted mb-3">{t("payout-no-active-plans")}</div>}

			{preview && <PayoutPreview preview={preview} t={t} dateFormat={dateFormat} />}

			<div className="d-flex justify-content-end">
				<Button
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
					onClick={close}
					disabled={loading || previewing}
				>
					{t("cancel")}
				</Button>

				<Button
					color="primary"
					type="submit"
					loading={loading || previewing}
					disabled={!plans.length || blocked}
					className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
				>
					{!preview ? t("payout-preview") : preview.scheduled ? t("schedule-payout") : t("pay-out")}
				</Button>
			</div>
		</Form>
	);
}

// what each plan pays out on the date, from settings PtoPayoutService
function PayoutPreview({ preview, t, dateFormat }) {
	const date = moment(preview.date).format(dateFormat);
	return (
		<div className="border rounded p-2 mb-3 small">
			<div className="font-weight-bold mb-2">
				{preview.scheduled ? t("payout-preview-scheduled", { date }) : t("payout-preview-title", { date })}
			</div>
			{preview.close && <div className="text-info mb-2">{t("payout-close-note", { date })}</div>}
			{preview.plans.map((plan) => (
				<div key={plan.userTimeOffPlan} className={`border-bottom pb-2 mb-2${plan.hours > 0 ? "" : " text-muted"}`}>
					<div className="d-flex justify-content-between font-weight-bold">
						<span>{plan.planCode}{plan.payCode?.code ? ` · ${plan.payCode.code}` : ""}</span>
						<span>{t("payout-col-pays")}: {hours(plan.hours)}</span>
					</div>
					<PreviewLine label={t("payout-col-locked-balance")} value={hours(plan.lockedBalance)} />
					<PreviewLine
						label={plan.openFrom
							? `${t("payout-col-accrued")} ${moment(plan.openFrom).format(dateFormat)} – ${moment(plan.openTo).format(dateFormat)}`
							: t("payout-col-accrued")}
						value={`+${hours(plan.earned)}`}
					/>
					<PreviewLine label={t("payout-col-used")} value={`-${hours(plan.used)}`} />
					{plan.requested > 0 && <PreviewLine label={t("payout-col-requested")} value={`-${hours(plan.requested)}`} />}
					{plan.approvedLater > 0 && <PreviewLine label={t("payout-approved-later")} value={`-${hours(plan.approvedLater)}`} />}
					<PreviewLine label={t("payout-max")} value={hours(plan.maxHours)} />
					{preview.close && plan.forfeited > 0 && (
						<PreviewLine label={t("payout-forfeited")} value={hours(plan.forfeited)} />
					)}
				</div>
			))}
			<div className="font-weight-bold">{t("payout-total", { hours: hours(preview.hours) })}</div>
			{preview.plans.map((plan) => [
				...plan.cancelRequests.map((request) => (
					<div key={request.id} className="text-warning">
						{t("payout-cancels-request", {
							plan: plan.planCode,
							date: moment(request.date).format(dateFormat),
							hours: hours(request.hours),
						})}
					</div>
				)),
				...plan.warnings
					.filter((warning) => !warning.includes("will be cancelled") && !warning.includes("are lost when it closes") && !warning.includes("stay on the plan for approved time off"))
					.map((warning) => (
						<div key={warning} className="text-warning">
							{plan.planCode}: {formatDatesInText(warning, dateFormat)}
						</div>
					)),
			])}
			{(preview.reasons || []).map((reason) => (
				<div key={reason} className="text-danger">{formatDatesInText(reason, dateFormat)}</div>
			))}
		</div>
	);
}

function PreviewLine({ label, value }) {
	return (
		<div className="d-flex justify-content-between">
			<span>{label}</span>
			<span>{value}</span>
		</div>
	);
}

export default PayoutForm;
